import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { feesService } from '../../services/feesService';
import { classService } from '../../services/classService';
import { useSelector } from 'react-redux';
import CustomDatePicker from '../ui/CustomDatePicker';
import { ChevronDown } from 'lucide-react';

const CreateFeesModal = ({ isOpen, onClose, onSuccess }) => {
  const { success, error } = useToast();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [classData, setClassData] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [isTermDropdownOpen, setIsTermDropdownOpen] = useState(false);
  const termDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (termDropdownRef.current && !termDropdownRef.current.contains(event.target)) {
        setIsTermDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [formData, setFormData] = useState({
    academicYear: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
    termNumber: 1,
    dueDate: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadClasses();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedClass) {
      loadClassData();
    }
  }, [selectedClass]);

  const loadClasses = async () => {
    try {
      const classesData = await classService.getAllClasses(user.token);
      setClasses(classesData);
    } catch (err) {
      console.error('Error loading classes:', err);
      error('Failed to load classes');
    }
  };

  const loadClassData = async () => {
    try {
      const response = await classService.getClassById(selectedClass, user.token);
      if (response.success) {
        setClassData(response.class);
      }
    } catch (err) {
      console.error('Error loading class data:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClass) {
      error('Please select a class');
      return;
    }

    if (!formData.dueDate) {
      error('Please select a due date');
      return;
    }

    setIsCreating(true);
    
    try {
      const bulkData = {
        classId: selectedClass,
        academicYear: formData.academicYear,
        termNumber: formData.termNumber,
        dueDate: formData.dueDate
      };

      const response = await feesService.createBulkFees(bulkData, user.token);
      
      if (response.success) {
        success(`Fees created for ${response.createdCount} students`);
        onSuccess();
        onClose();
        resetForm();
      } else {
        error(response.message || 'Failed to create fees');
      }
    } catch (err) {
      console.error('Error creating fees:', err);
      error('Failed to create fees');
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      academicYear: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
      termNumber: 1,
      dueDate: ''
    });
    setSelectedClass('');
    setClassData(null);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const calculateFeePerTerm = () => {
    if (classData && classData.feesPerAcademicYear && classData.numberOfTerms) {
      return Math.round((classData.feesPerAcademicYear / classData.numberOfTerms) * 100) / 100;
    }
    return 0;
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isOpen ? 'block' : 'hidden'}`}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create Fees for Class</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Class</label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300"
                required
              >
                <option value="">Choose a class</option>
              {classes.map(cls => (
                <option key={cls._id} value={cls._id}>
                  {cls.name} - Section {cls.section}
                </option>
              ))}
            </select>
          </div>

          {classData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Class Fee Information</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Annual Fees:</span>
                  <p className="font-medium text-blue-800">₹{classData.feesPerAcademicYear?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-blue-700">Number of Terms:</span>
                  <p className="font-medium text-blue-800">{classData.numberOfTerms}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-blue-700">Fee per Term:</span>
                  <p className="font-medium text-blue-800">₹{calculateFeePerTerm().toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year</label>
              <input
                type="text"
                value={formData.academicYear}
                onChange={(e) => setFormData({...formData, academicYear: e.target.value})}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 font-medium text-slate-700"
                placeholder="2024-2025"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Term Number</label>
              <div className="relative" ref={termDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsTermDropdownOpen(!isTermDropdownOpen)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl flex items-center justify-between bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 group shadow-sm text-sm min-h-[42px]"
                  disabled={!classData}
                >
                  <span className={`font-bold ${classData ? 'text-slate-700' : 'text-slate-400'}`}>
                    {classData ? `Term ${formData.termNumber}` : 'Select Class First'}
                  </span>
                  <ChevronDown 
                    size={18} 
                    className={`text-slate-400 transition-transform duration-300 ${isTermDropdownOpen ? 'rotate-180 text-blue-500' : ''}`}
                  />
                </button>

                {isTermDropdownOpen && classData && (
                  <div className="absolute z-[100] w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-blue-900/10 overflow-hidden animate-fadeIn backdrop-blur-xl">
                    <div className="max-h-[200px] overflow-y-auto custom-scrollbar p-1.5">
                      {Array.from({ length: classData.numberOfTerms }, (_, i) => (
                        <button
                          key={i + 1}
                          type="button"
                          onClick={() => {
                            setFormData({...formData, termNumber: i + 1});
                            setIsTermDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm font-bold rounded-xl transition-all duration-200 mb-0.5 ${formData.termNumber === (i + 1) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                          Term {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <CustomDatePicker
              label="Due Date"
              name="dueDate"
              value={formData.dueDate}
              onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
              required={true}
            />
          </div>

          {classData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-800 mb-2">Fee Creation Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-700">Class:</span>
                  <span className="font-medium text-green-800">{classData.name} - Section {classData.section}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Academic Year:</span>
                  <span className="font-medium text-green-800">{formData.academicYear}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Term:</span>
                  <span className="font-medium text-green-800">Term {formData.termNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Amount per Student:</span>
                  <span className="font-medium text-green-800">₹{calculateFeePerTerm().toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Due Date:</span>
                  <span className="font-medium text-green-800">
                    {formData.dueDate ? new Date(formData.dueDate).toLocaleDateString() : 'Not set'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 active:scale-95"
              disabled={isCreating || !selectedClass}
            >
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <span>Create Fees</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFeesModal;
